import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import SmsIcon from "@mui/icons-material/Sms";
import SecurityIcon from "@mui/icons-material/Security";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BlockIcon from "@mui/icons-material/Block";
import { useHistory } from "react-router-dom";
import * as authMod from "firebase/auth";
import QRCode from "qrcode";

import { ModularAuth } from "../../../../components/FirebaseAuth/firebase";
import UserPageLayout from "../../../../components/User/UserPageLayout";
import Loader from "../../../../components/Loader";
import { recordUserActivity } from "../../../../libs/activityLog";
import { appConfig } from "../../../../config/appConfig";

const steps = ["Method", "Setup", "Verify"];
const cleanAuthError = (error, fallback) => {
  const raw = String(error?.message || "").trim();
  if (!raw) return fallback;
  const withoutPrefix = raw.replace(/^Firebase:\s*/i, "");
  const withoutCode = withoutPrefix.replace(/\s*\(auth\/[^)]+\)\.?$/i, "");
  return withoutCode || fallback;
};
const friendlyMfaError = (error, fallback) => {
  const code = error?.code || "";
  const serverResponse =
    error?.customData?._serverResponse ||
    error?.customData?._tokenResponse?.error?.message ||
    error?.customData?.serverResponse ||
    "";
  let serverMessage = "";
  try {
    if (serverResponse) {
      if (typeof serverResponse === "string" && serverResponse.trim().startsWith("{")) {
        const parsed = JSON.parse(serverResponse);
        serverMessage = parsed?.error?.message || "";
      } else {
        serverMessage = String(serverResponse);
      }
    }
  } catch (_e) {
    serverMessage = String(serverResponse || "");
  }

  if (code === "auth/internal-error") {
    return `Authenticator setup failed. Confirm TOTP is enabled in Identity Platform, email is verified, and re-authenticate from Profile before retrying.${serverMessage ? ` Server: ${serverMessage}.` : ""}`;
  }
  if (code === "auth/requires-recent-login") {
    return "For security, please re-authenticate and try again.";
  }
  if (code === "auth/unverified-email") {
    return "Please verify your email address before enabling authenticator MFA.";
  }
  return cleanAuthError(error, fallback);
};
const buildOtpAuthUrl = (secretKey, account, issuer) => {
  const label = `${issuer}:${account}`;
  return `otpauth://totp/${encodeURIComponent(label)}?secret=${encodeURIComponent(secretKey)}&issuer=${encodeURIComponent(issuer)}&algorithm=SHA1&digits=6&period=30`;
};
const buildQrDataUrl = async (otpAuthUrl) => {
  return QRCode.toDataURL(otpAuthUrl, {
    errorCorrectionLevel: "M",
    margin: 1,
    width: 220,
  });
};
const startTotpEnrollmentViaRest = async (auth, user) => {
  const apiKey = auth?.app?.options?.apiKey;
  if (!apiKey || !user) {
    throw new Error("Firebase API key or user session is missing.");
  }

  const idToken = await user.getIdToken(true);
  const response = await fetch(
    `https://identitytoolkit.googleapis.com/v2/accounts/mfaEnrollment:start?key=${encodeURIComponent(apiKey)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        idToken,
        totpEnrollmentInfo: {},
      }),
    }
  );

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const backendMessage = data?.error?.message || `HTTP_${response.status}`;
    throw new Error(`Identity Platform TOTP start failed: ${backendMessage}`);
  }

  const sessionInfo = data?.totpSessionInfo?.sessionInfo || "";
  const secretKey = data?.totpSessionInfo?.sharedSecretKey || "";
  if (!sessionInfo || !secretKey) {
    throw new Error("Identity Platform did not return TOTP session info.");
  }

  return { sessionInfo, secretKey };
};
const finalizeTotpEnrollmentViaRest = async (auth, user, sessionInfo, verificationCode) => {
  const apiKey = auth?.app?.options?.apiKey;
  if (!apiKey || !user) {
    throw new Error("Firebase API key or user session is missing.");
  }

  const idToken = await user.getIdToken(true);
  const response = await fetch(
    `https://identitytoolkit.googleapis.com/v2/accounts/mfaEnrollment:finalize?key=${encodeURIComponent(apiKey)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        idToken,
        displayName: "Authenticator App",
        totpVerificationInfo: {
          sessionInfo,
          verificationCode,
        },
      }),
    }
  );

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const backendMessage = data?.error?.message || `HTTP_${response.status}`;
    throw new Error(`Identity Platform TOTP finalize failed: ${backendMessage}`);
  }
};
const diagnoseTotpStart = async (auth, user) => {
  const apiKey = auth?.app?.options?.apiKey;
  const projectId = auth?.app?.options?.projectId || "";
  const authDomain = auth?.app?.options?.authDomain || "";
  if (!apiKey || !user) return "";

  try {
    const idToken = await user.getIdToken(true);
    const response = await fetch(
      `https://identitytoolkit.googleapis.com/v2/accounts/mfaEnrollment:start?key=${encodeURIComponent(apiKey)}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          idToken,
          totpEnrollmentInfo: {},
        }),
      }
    );

    const data = await response.json().catch(() => ({}));
    const backendMessage = data?.error?.message || "";
    if (response.ok) {
      return ` Runtime config: projectId=${projectId || "unknown"}, authDomain=${authDomain || "unknown"}. REST mfaEnrollment:start succeeded.`;
    }

    return ` Runtime config: projectId=${projectId || "unknown"}, authDomain=${authDomain || "unknown"}. Identity Platform detail: ${backendMessage || `HTTP_${response.status}`}.`;
  } catch (diagnosticError) {
    const reason = diagnosticError?.message || "network_error";
    return ` Runtime config: projectId=${projectId || "unknown"}, authDomain=${authDomain || "unknown"}. Diagnostic call failed: ${reason}.`;
  }
};
const maskPhone = (phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return "";
  return `***${digits.slice(-4)}`;
};
const factorLabel = (factor) => {
  if (!factor) return "Unknown MFA Device";
  if (factor.factorId === authMod.PhoneMultiFactorGenerator.FACTOR_ID) {
    return `SMS (${maskPhone(factor.phoneNumber) || "phone"})`;
  }
  if (factor.factorId === "totp") {
    return "Authenticator App (TOTP)";
  }
  return factor.displayName || factor.factorId || "MFA Device";
};

const mfaCardSx = {
  borderRadius: 2,
  borderColor: "#dadce0",
  overflow: "hidden",
  boxShadow: "0 18px 48px rgba(60,64,67,0.08)",
};

const mfaIconSx = {
  width: 58,
  height: 58,
  borderRadius: 2,
  bgcolor: "#e8f0fe",
  color: "#1a73e8",
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
};

const mfaButtonSx = {
  textTransform: "none",
  fontWeight: 800,
  borderRadius: 1.5,
};

const MFAEnroll = () => {
  const history = useHistory();
  const recaptchaRef = useRef(null);

  const [activeStep, setActiveStep] = useState(0);
  const [method, setMethod] = useState("sms");

  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationId, setVerificationId] = useState("");
  const [verificationCode, setVerificationCode] = useState("");

  const [totpSecret, setTotpSecret] = useState(null);
  const [totpRestSessionInfo, setTotpRestSessionInfo] = useState("");
  const [totpQrDataUrl, setTotpQrDataUrl] = useState("");
  const [totpCode, setTotpCode] = useState("");

  const [loading, setLoading] = useState(false);
  const [checkingFactors, setCheckingFactors] = useState(true);
  const [existingFactors, setExistingFactors] = useState([]);
  const [busyFactorUid, setBusyFactorUid] = useState("");
  const [disablingAllFactors, setDisablingAllFactors] = useState(false);
  const [message, setMessage] = useState({ type: "info", text: "" });

  const auth = ModularAuth;
  const getActiveUser = useCallback(async () => {
    if (typeof auth.authStateReady === "function") {
      await auth.authStateReady();
    }
    return auth.currentUser;
  }, [auth]);

  const hasTotpSupport = useMemo(() => {
    return Boolean(
      authMod?.TotpMultiFactorGenerator?.generateSecret &&
      authMod?.multiFactor
    );
  }, []);

  useEffect(() => {
    const checkUser = async () => {
      const currentUser = await getActiveUser();
      if (!currentUser) {
        history.replace("/signin");
        return;
      }
      await currentUser.reload();
      const factors = authMod.multiFactor(currentUser)?.enrolledFactors || [];
      setExistingFactors(factors);
      setCheckingFactors(false);
    };
    checkUser();

    return () => {
      if (recaptchaRef.current) {
        recaptchaRef.current.clear();
        recaptchaRef.current = null;
      }
    };
  }, [getActiveUser, history]);

  const ensureRecaptcha = async () => {
    if (recaptchaRef.current) return recaptchaRef.current;

    const verifier = new authMod.RecaptchaVerifier(auth, "mfa-enroll-recaptcha", {
      size: "normal",
    });
    await verifier.render();
    recaptchaRef.current = verifier;
    return verifier;
  };

  const createActivityLog = async (action, description) => {
    const currentUser = await getActiveUser();
    if (!currentUser) return;
    await recordUserActivity(action, description);
  };

  const deleteFactorAndReturn = async (factor) => {
    const currentUser = await getActiveUser();
    if (!currentUser || !factor?.uid) return;
    setBusyFactorUid(factor.uid);
    setMessage({ type: "info", text: "" });
    try {
      await authMod.multiFactor(currentUser).unenroll(factor.uid);
      await createActivityLog("mfa device removed", `${factorLabel(factor)} removed`);
      history.push("/user/security");
    } catch (error) {
      setMessage({ type: "error", text: friendlyMfaError(error, "Failed to remove MFA device.") });
    } finally {
      setBusyFactorUid("");
    }
  };

  const disableMfaAndReturn = async () => {
    const currentUser = await getActiveUser();
    if (!currentUser) return;
    const factors = authMod.multiFactor(currentUser)?.enrolledFactors || [];
    if (!factors.length) {
      history.push("/user/security");
      return;
    }
    setDisablingAllFactors(true);
    setMessage({ type: "info", text: "" });
    try {
      for (const factor of factors) {
        await authMod.multiFactor(currentUser).unenroll(factor.uid);
      }
      await createActivityLog("mfa disabled", "All MFA devices removed");
      history.push("/user/security");
    } catch (error) {
      setMessage({ type: "error", text: friendlyMfaError(error, "Failed to disable MFA.") });
    } finally {
      setDisablingAllFactors(false);
    }
  };

  const handleNextFromMethod = () => {
    if (method === "authenticator" && !hasTotpSupport) {
      setMessage({ type: "error", text: "Authenticator (TOTP) is not available in the installed Firebase SDK. Upgrade firebase and rebuild the app." });
      return;
    }
    setMessage({ type: "info", text: "" });
    setActiveStep(1);
  };

  const sendSmsCode = async () => {
    const currentUser = await getActiveUser();
    if (!currentUser) return;
    if (!phoneNumber) {
      setMessage({ type: "error", text: "Enter a phone number in E.164 format, for example +15555550100." });
      return;
    }

    setLoading(true);
    setMessage({ type: "info", text: "" });

    try {
      const session = await authMod.multiFactor(currentUser).getSession();
      const verifier = await ensureRecaptcha();
      const provider = new authMod.PhoneAuthProvider(auth);
      const id = await provider.verifyPhoneNumber(
        { phoneNumber, session },
        verifier
      );
      setVerificationId(id);
      setActiveStep(2);
      setMessage({ type: "success", text: "Verification code sent to your phone." });
    } catch (error) {
      setMessage({ type: "error", text: friendlyMfaError(error, "Failed to send SMS verification code.") });
    } finally {
      setLoading(false);
    }
  };

  const prepareTotpSetup = useCallback(async () => {
    const currentUser = await getActiveUser();
    if (!currentUser) return;

    if (!currentUser.emailVerified) {
      setMessage({ type: "error", text: "Please verify your email address first, then retry authenticator setup." });
      return;
    }

    setLoading(true);
    setMessage({ type: "info", text: "" });

    try {
      const generator = authMod.TotpMultiFactorGenerator;
      if (!generator?.generateSecret) {
        throw new Error("Authenticator (TOTP) is not available in the installed Firebase SDK. Upgrade firebase package and rebuild.");
      }

      let session = await authMod.multiFactor(currentUser).getSession();
      let secret;
      try {
        secret = await generator.generateSecret(session);
      } catch (firstError) {
        if (firstError?.code === "auth/internal-error") {
          await currentUser.getIdToken(true);
          await currentUser.reload();
          const refreshedUser = await getActiveUser();
          if (!refreshedUser) throw firstError;
          session = await authMod.multiFactor(refreshedUser).getSession();
          try {
            secret = await generator.generateSecret(session);
          } catch (_secondError) {
            // Fallback: REST start flow when SDK TOTP secret generation fails.
            const fallback = await startTotpEnrollmentViaRest(auth, refreshedUser);
            secret = { secretKey: fallback.secretKey, source: "rest" };
            setTotpRestSessionInfo(fallback.sessionInfo);
          }
        } else {
          throw firstError;
        }
      }

      const issuer = appConfig.mfaIssuer;
      const account = currentUser.email || currentUser.uid;
      let qrDataUrl = "";
      if (secret?.secretKey) {
        const otpAuthUrl = buildOtpAuthUrl(secret.secretKey, account, issuer);
        qrDataUrl = await buildQrDataUrl(otpAuthUrl);
      }

      setTotpSecret(secret);
      setTotpQrDataUrl(qrDataUrl);
      setMessage({ type: "success", text: "Scan the QR code with your authenticator app, then click Next to verify with a code." });
    } catch (error) {
      // Keep raw details in console for project-level Identity Platform troubleshooting.
      console.error("TOTP enrollment setup error:", {
        code: error?.code,
        message: error?.message,
        customData: error?.customData,
      });
      let diagnosticSuffix = "";
      if (error?.code === "auth/internal-error") {
        diagnosticSuffix = await diagnoseTotpStart(auth, currentUser);
      }
      if (diagnosticSuffix) {
        console.error("TOTP enrollment diagnostic:", diagnosticSuffix);
      }
      const details = error?.code ? ` (${error.code})` : "";
      setMessage({
        type: "error",
        text: `${friendlyMfaError(error, "Failed to initialize authenticator setup.")}${diagnosticSuffix}${details}`,
      });
    } finally {
      setLoading(false);
    }
  }, [auth, getActiveUser]);

  useEffect(() => {
    if (activeStep === 1 && method === "authenticator" && !totpSecret && !loading) {
      prepareTotpSetup();
    }
  }, [activeStep, loading, method, prepareTotpSetup, totpSecret]);

  const verifySmsAndEnroll = async () => {
    const currentUser = await getActiveUser();
    if (!currentUser || !verificationId || !verificationCode) return;

    setLoading(true);
    setMessage({ type: "info", text: "" });

    try {
      const credential = authMod.PhoneAuthProvider.credential(verificationId, verificationCode);
      const assertion = authMod.PhoneMultiFactorGenerator.assertion(credential);
      await authMod.multiFactor(currentUser).enroll(assertion, "SMS Phone");

      await createActivityLog("mfa enrolled", "User enrolled SMS MFA factor");
      history.push("/user/security");
    } catch (error) {
      setMessage({ type: "error", text: friendlyMfaError(error, "Failed to verify SMS code.") });
    } finally {
      setLoading(false);
    }
  };

  const verifyTotpAndEnroll = async () => {
    const currentUser = await getActiveUser();
    if (!currentUser || !totpSecret || !totpCode) return;

    setLoading(true);
    setMessage({ type: "info", text: "" });

    try {
      const generator = authMod.TotpMultiFactorGenerator;
      if (totpRestSessionInfo) {
        await finalizeTotpEnrollmentViaRest(auth, currentUser, totpRestSessionInfo, totpCode);
      } else {
        if (!generator?.assertionForEnrollment) {
          throw new Error("Authenticator verification is not available in the installed Firebase SDK.");
        }
        const assertion = generator.assertionForEnrollment(totpSecret, totpCode);
        await authMod.multiFactor(currentUser).enroll(assertion, "Authenticator App");
      }

      try {
        await currentUser.getIdToken(true);
        await currentUser.reload();
      } catch (_e) {
        // Non-fatal after successful enrollment
      }

      await createActivityLog("mfa enrolled", "User enrolled authenticator MFA factor");
      history.push("/user/security");
    } catch (error) {
      setMessage({ type: "error", text: friendlyMfaError(error, "Failed to verify authenticator code.") });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (activeStep === 0) {
      history.push("/user/security");
      return;
    }
    setMessage({ type: "info", text: "" });
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  return (
    <UserPageLayout title="Security">
      <Box sx={{ px: { xs: 1.5, md: 2 }, py: 2 }}>
      <Card variant="outlined" sx={mfaCardSx}>
        <Box sx={{ height: 4, background: "linear-gradient(90deg, #05690D, #1976D2)" }} />
        <CardContent sx={{ p: 0 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            sx={{
              px: { xs: 2, md: 3 },
              py: { xs: 2.25, md: 2.75 },
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={mfaIconSx}>
                <SecurityIcon sx={{ fontSize: 30 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 900, color: "#30343b" }}>
                  Multi-factor authentication
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Add or manage an extra sign-in step for your account.
                </Typography>
              </Box>
            </Stack>
            <Chip label="Security" color="primary" variant="outlined" sx={{ fontWeight: 800 }} />
          </Stack>
          <Box sx={{ p: { xs: 2, md: 3 } }}>
          {checkingFactors ? (
            <Loader variant="section" label="Loading MFA settings..." />
          ) : existingFactors.length > 0 ? (
            <Stack spacing={2.25}>
              {message.text && (
                <Alert severity={message.type}>
                  {message.text}
                </Alert>
              )}
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                Enrolled methods
              </Typography>
              <Typography variant="body2" color="text.secondary">
                These methods can be used to verify sensitive account actions. Remove a device or disable MFA entirely.
              </Typography>

              {existingFactors.map((factor) => (
                <Paper key={factor.uid} variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: "#dadce0" }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box sx={{ ...mfaIconSx, width: 44, height: 44, borderRadius: 1.5 }}>
                        {factor.factorId === authMod.PhoneMultiFactorGenerator.FACTOR_ID ? (
                          <SmsIcon fontSize="small" />
                        ) : (
                          <QrCode2Icon fontSize="small" />
                        )}
                      </Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                          {factorLabel(factor)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {factor.displayName || factor.uid}
                        </Typography>
                      </Box>
                    </Stack>
                    <Button
                      color="error"
                      variant="outlined"
                      startIcon={busyFactorUid === factor.uid ? <Loader size={16} /> : <DeleteOutlineIcon />}
                      onClick={() => deleteFactorAndReturn(factor)}
                      disabled={busyFactorUid === factor.uid || disablingAllFactors}
                      sx={mfaButtonSx}
                    >
                      Delete
                    </Button>
                  </Stack>
                </Paper>
              ))}

              <Divider />
              <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }}>
                <Button variant="outlined" onClick={() => history.push("/user/security")} sx={mfaButtonSx}>
                  Back to Security
                </Button>
                <Button
                  color="error"
                  variant="outlined"
                  startIcon={disablingAllFactors ? <Loader size={16} /> : <BlockIcon />}
                  onClick={disableMfaAndReturn}
                  disabled={disablingAllFactors || !!busyFactorUid}
                  sx={mfaButtonSx}
                >
                  Disable MFA
                </Button>
              </Stack>
            </Stack>
          ) : (
            <>
          <Stepper activeStep={activeStep} sx={{ mb: 3, "& .MuiStepLabel-label": { fontWeight: 800 } }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {message.text && (
            <Alert severity={message.type} sx={{ mb: 2 }}>
              {message.text}
            </Alert>
          )}

          {activeStep === 0 && (
            <Stack spacing={2}>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                Choose a verification method
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pick the second factor you want to use for stronger verification.
              </Typography>

              <RadioGroup value={method} onChange={(e) => setMethod(e.target.value)}>
                <Card
                  variant="outlined"
                  sx={{
                    mb: 1.5,
                    borderRadius: 2,
                    borderColor: method === "sms" ? "#1a73e8" : "#dadce0",
                    bgcolor: method === "sms" ? "#e8f0fe" : "background.paper",
                  }}
                >
                  <CardContent>
                    <FormControlLabel
                      value="sms"
                      control={<Radio />}
                      label={
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <SmsIcon fontSize="small" />
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                              SMS
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Receive a one-time code by text message.
                            </Typography>
                          </Box>
                        </Stack>
                      }
                    />
                  </CardContent>
                </Card>

                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    borderColor: method === "authenticator" ? "#1a73e8" : "#dadce0",
                    bgcolor: method === "authenticator" ? "#e8f0fe" : "background.paper",
                  }}
                >
                  <CardContent>
                    <FormControlLabel
                      value="authenticator"
                      control={<Radio />}
                      label={
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <QrCode2Icon fontSize="small" />
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                              Authenticator app
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Scan a QR code with Google Authenticator, 1Password, etc.
                            </Typography>
                          </Box>
                        </Stack>
                      }
                    />
                  </CardContent>
                </Card>
              </RadioGroup>

              <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} justifyContent="space-between" sx={{ pt: 1 }}>
                <Button variant="outlined" onClick={handleBack} sx={mfaButtonSx}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={handleNextFromMethod} sx={mfaButtonSx}>
                  Continue
                </Button>
              </Stack>
            </Stack>
          )}

          {activeStep === 1 && method === "sms" && (
            <Stack spacing={2}>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                Set up SMS verification
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enter your phone number in international format, then request a setup code.
              </Typography>

              <TextField
                label="Phone number"
                placeholder="+15555550100"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                fullWidth
              />

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: "#dadce0" }}>
                <div id="mfa-enroll-recaptcha" />
              </Paper>

              <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} justifyContent="space-between" sx={{ pt: 1 }}>
                <Button variant="outlined" onClick={handleBack} sx={mfaButtonSx}>
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={sendSmsCode}
                  disabled={loading}
                  startIcon={loading ? <Loader size={16} /> : <SmsIcon />}
                  sx={mfaButtonSx}
                >
                  Send code
                </Button>
              </Stack>
            </Stack>
          )}

          {activeStep === 1 && method === "authenticator" && (
            <Stack spacing={2}>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                Set up authenticator app
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Scan the QR code with your authenticator app, or use the manual setup key.
              </Typography>

              {loading ? (
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, borderColor: "#dadce0" }}>
                  <Loader variant="section" label="Preparing QR code..." />
                </Paper>
              ) : (
                <>
                  {totpSecret && totpQrDataUrl ? (
                    <Box sx={{ textAlign: "center", p: 2, border: "1px solid", borderColor: "#dadce0", borderRadius: 2 }}>
                      <img
                        src={totpQrDataUrl}
                        alt="Authenticator QR"
                        style={{ width: 220, height: 220, maxWidth: "100%" }}
                      />
                    </Box>
                  ) : totpSecret ? (
                    <Alert severity="warning">QR code could not be generated locally. Use the manual setup key below.</Alert>
                  ) : message.type !== "error" ? (
                    <Alert severity="info">Preparing authenticator setup...</Alert>
                  ) : (
                    <Box />
                  )}

                  {totpSecret?.secretKey && (
                    <TextField
                      label="Manual setup key"
                      value={totpSecret.secretKey}
                      fullWidth
                      InputProps={{ readOnly: true }}
                    />
                  )}
                </>
              )}

              <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} justifyContent="space-between" sx={{ pt: 1 }}>
                <Button variant="outlined" onClick={handleBack} sx={mfaButtonSx}>
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setActiveStep(2)}
                  disabled={loading || !totpSecret}
                  sx={mfaButtonSx}
                >
                  Continue
                </Button>
              </Stack>
            </Stack>
          )}

          {activeStep === 2 && method === "sms" && (
            <Stack spacing={2}>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                Verify SMS code
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enter the code sent to your phone to complete enrollment.
              </Typography>

              <TextField
                label="Verification code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }}
                fullWidth
              />

              <Divider />
              <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }}>
                <Button variant="outlined" onClick={handleBack} sx={mfaButtonSx}>
                  Back
                </Button>
                <Button
                  variant="contained"
                  startIcon={loading ? <Loader size={16} /> : <SecurityIcon />}
                  onClick={verifySmsAndEnroll}
                  disabled={loading || !verificationCode}
                  sx={mfaButtonSx}
                >
                  Verify and finish
                </Button>
              </Stack>
            </Stack>
          )}

          {activeStep === 2 && method === "authenticator" && (
            <Stack spacing={2}>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                Verify authenticator code
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enter the current code from your authenticator app to complete enrollment.
              </Typography>

              <TextField
                label="Authenticator code"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }}
                fullWidth
              />

              <Divider />
              <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }}>
                <Button variant="outlined" onClick={handleBack} sx={mfaButtonSx}>
                  Back
                </Button>
                <Button
                  variant="contained"
                  startIcon={loading ? <Loader size={16} /> : <SecurityIcon />}
                  onClick={verifyTotpAndEnroll}
                  disabled={loading || !totpCode}
                  sx={mfaButtonSx}
                >
                  Verify and finish
                </Button>
              </Stack>
            </Stack>
          )}
            </>
          )}
          </Box>
        </CardContent>
      </Card>
      </Box>
    </UserPageLayout>
  );
};

export default MFAEnroll;
